import { useTranslate } from '@va/localization';
import { DateInput, InputMessage } from '@va/ui/design-system';
import { Dayjs } from 'dayjs';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { filterValidationKeys } from '../../constants';
import { FilterInputComponentRefType } from '../../filter-types';
import { useFiltersContext } from '../../filters-context';
import { useFilterValidation } from '../../useFilterValidation';
import { PickOperatorBtn } from '../PickOperatorBtn';
import { useOperator } from '../useOperator';
import { DATE_FILTER_VALUE_FORMAT, DateFilter, DateFilterInputProps, parseDateFilterValue } from './DateFilter';

type DateFilterDraft = { start: Dayjs | undefined; end: Dayjs | undefined };

export const DateFilterInput = forwardRef<FilterInputComponentRefType, { filter: DateFilter } & DateFilterInputProps>(
  ({ filter, startLabel, endLabel, pickerConfig, defaultEnd }, ref) => {
    const [start, setStart] = useState<Dayjs | undefined>(parseDateFilterValue(filter.values[0]?.start));
    const [end, setEnd] = useState<Dayjs | undefined>(() => parseDateFilterValue(filter.values[0]?.end) ?? defaultEnd);

    const { operator, setOperator } = useOperator(filter.operator);
    const { applySingleNewFilter, isFilterApplied, updateAppliedFilter } = useFiltersContext();
    const translate = useTranslate();

    const onSubmit = useCallback(() => {
      if (!start || !end) return;

      const values = [{ start: start.format(DATE_FILTER_VALUE_FORMAT), end: end.format(DATE_FILTER_VALUE_FORMAT) }];

      if (isFilterApplied(filter.id)) {
        updateAppliedFilter(filter.id, { values, operator });
        return;
      }

      const clone = Object.assign({}, filter);
      clone.values = values;
      clone.operator = operator;
      applySingleNewFilter(clone);
    }, [applySingleNewFilter, end, filter, isFilterApplied, operator, start, updateAppliedFilter]);

    const { validate, handleSubmit, error } = useFilterValidation<DateFilterDraft, { start: string; end: string }>({
      onSubmit,
      value: { start, end },
      validationFunc: (val) => {
        if (val.start && val.end) return;

        return {
          start: val.start ? '' : filterValidationKeys.required,
          end: val.end ? '' : filterValidationKeys.required,
        };
      },
    });

    useImperativeHandle(ref, () => ({ submit: handleSubmit }));

    return (
      <div className='space-y-1'>
        <PickOperatorBtn
          onOperatorChange={setOperator}
          operatorOptions={filter.operatorOptions}
          selectedOperator={operator}
        />
        <div>
          <DateInput
            value={start}
            label={startLabel}
            // Bounding each picker by the other one keeps the range from being inverted.
            pickerConfig={{ ...pickerConfig, maxDate: end ?? pickerConfig?.maxDate }}
            onChange={(val) => {
              setStart(val);
              validate({ start: val, end });
            }}
          />
          <InputMessage error={translate(error?.start ?? '')} />
        </div>
        <div>
          <DateInput
            value={end}
            label={endLabel}
            pickerConfig={{ ...pickerConfig, minDate: start ?? pickerConfig?.minDate }}
            onChange={(val) => {
              setEnd(val);
              validate({ start, end: val });
            }}
          />
          <InputMessage error={translate(error?.end ?? '')} />
        </div>
      </div>
    );
  },
);
